import React from "react";
import * as Mui from "@mui/material";
import {
  AppsRounded,
  FolderRounded,
  GroupsRounded,
  HomeRounded,
  KeyboardArrowRight,
  PeopleAltRounded,
  ReceiptLongRounded,
  Search,
  SettingsRounded,
  ShieldRounded,
  TrackChangesRounded,
  TuneRounded,
} from "@mui/icons-material";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  designColors,
  designFontFamily,
} from "services/constants/designTokens";

// Shared building blocks for the Roles screens (list, View Role panel, and
// the Create/Edit permission form). Everything here is presentational or a
// pure helper over the role payload shape returned by
// product-control/role/ and product-control/products/summary/:
//   product (module group, e.g. "My Workspace")
//     -> module_details[] (module)
//       -> submodules[] ({ sub_module_name, permissions, *_applicable })

export const teal = "#0F766E";
export const tealCheck = "#0F8A83";

// ---------------------------------------------------------------------------
// Permission helpers
// ---------------------------------------------------------------------------

export const isSubmoduleEnabled = (submodule) =>
  Boolean(
    submodule?.permissions?.viewer ||
      submodule?.permissions?.editor ||
      submodule?.permissions?.first_level_approver ||
      submodule?.permissions?.second_level_approver,
  );

// A submodule counts as "selected" only when it holds a grant and hasn't been
// hidden by its own module being unchecked -- the same rule the permission
// form's row checkbox uses.
export const isSubmoduleSelected = (submodule) =>
  isSubmoduleEnabled(submodule) && submodule?.view_applicable !== false;

export const getProductTotalCount = (product) =>
  (product?.module_details ?? []).reduce(
    (total, module) => total + (module?.submodules ?? []).length,
    0,
  );

export const getProductSelectedCount = (product) =>
  (product?.module_details ?? []).reduce(
    (total, module) =>
      total + (module?.submodules ?? []).filter(isSubmoduleEnabled).length,
    0,
  );

// Groups a product's submodules for display. A module whose only submodule
// carries its own name is shown as a single flat row -- a group header above
// it would just repeat the same label.
export const groupProductRows = (product, search = "") => {
  const query = search.trim().toLowerCase();
  return (product?.module_details ?? [])
    .map((module) => {
      const moduleMatches = module?.module_name?.toLowerCase().includes(query);
      const submodules = (module?.submodules ?? []).filter(
        (submodule) =>
          !query ||
          moduleMatches ||
          submodule?.sub_module_name?.toLowerCase().includes(query),
      );
      const flat =
        (module?.submodules ?? []).length === 1 &&
        module?.submodules?.[0]?.sub_module_name === module?.module_name;
      return { module, submodules, showHeader: !flat };
    })
    .filter((group) => group.submodules.length > 0);
};

export const getSubmoduleDescription = (submodule) =>
  submodule?.description || submodule?.sub_module_description || "";

// The list and view share status normalization for label/boolean API fields.
// Older records without a status retain the Active default.
export { getRoleStatus } from "./roleStatus";

// User groups assign a role per company. Match by role_id, falling back to
// the role name for payloads that only carry the name.
export const getGroupsForRole = (userGroups, role) =>
  (userGroups ?? [])
    .map((group) => {
      const matches = (group?.assignments ?? []).filter((assignment) =>
        assignment?.role_id !== undefined && assignment?.role_id !== null
          ? String(assignment.role_id) === String(role?.role_id)
          : assignment?.role_name === role?.role_name,
      );
      return matches.length
        ? { ...group, companyCount: matches.length }
        : null;
    })
    .filter(Boolean);

// ---------------------------------------------------------------------------
// Visuals
// ---------------------------------------------------------------------------

const PRODUCT_VISUALS = [
  { match: ["my workspace", "my zone", "myzone"], Icon: HomeRounded, color: "#1D4ED8", bg: "#EFF6FF" },
  { match: ["team"], Icon: GroupsRounded, color: "#2563EB", bg: "#EFF6FF" },
  { match: ["payroll"], Icon: ReceiptLongRounded, color: "#C026D3", bg: "#FDF4FF" },
  { match: ["operation"], Icon: SettingsRounded, color: "#7C3AED", bg: "#F5F3FF" },
  { match: ["people", "hr"], Icon: PeopleAltRounded, color: "#16A34A", bg: "#F0FDF4" },
  { match: ["crm"], Icon: TrackChangesRounded, color: "#DC2626", bg: "#FEF2F2" },
  { match: ["project"], Icon: FolderRounded, color: "#EA580C", bg: "#FFF7ED" },
  { match: ["admin"], Icon: ShieldRounded, color: "#2563EB", bg: "#EFF6FF" },
  { match: ["system", "config"], Icon: TuneRounded, color: "#D97706", bg: "#FFFBEB" },
];

export const getProductVisual = (name = "") => {
  const lower = String(name).toLowerCase();
  return (
    PRODUCT_VISUALS.find((visual) =>
      visual.match.some((token) =>
        token === "hr" ? lower.split(/\s+/).includes("hr") : lower.includes(token),
      ),
    ) || { Icon: AppsRounded, color: teal, bg: "#E6F8F4" }
  );
};

export const ProductIcon = ({ name, size = 36 }) => {
  const { Icon, color, bg } = getProductVisual(name);
  return (
    <Mui.Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "10px",
        backgroundColor: bg,
        color,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: size * 0.58 }} />
    </Mui.Box>
  );
};

// Rounded square that holds a section's leading icon ("Role Details",
// "Modules & Permissions", ...).
export const SectionIcon = ({ icon, color = teal, bg = "#E6F8F4", size = 44 }) => (
  <Mui.Box
    sx={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: "12px",
      backgroundColor: bg,
      color,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiSvgIcon-root": { fontSize: size * 0.55 },
    }}
  >
    {icon}
  </Mui.Box>
);

export const SectionHeading = ({ icon, iconColor, iconBg, title, subtitle, action }) => (
  <Mui.Stack
    direction={{ xs: "column", md: "row" }}
    spacing={1.5}
    alignItems={{ xs: "stretch", md: "center" }}
    justifyContent="space-between"
  >
    <Mui.Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
      {icon ? <SectionIcon icon={icon} color={iconColor} bg={iconBg} /> : null}
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography sx={roleKitType.sectionTitle}>{title}</Mui.Typography>
        {subtitle ? (
          <Mui.Typography sx={roleKitType.sectionSubtitle}>{subtitle}</Mui.Typography>
        ) : null}
      </Mui.Box>
    </Mui.Stack>
    {action}
  </Mui.Stack>
);

export const roleKitType = {
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    fontFamily: designFontFamily,
    color: designColors.slate800,
    lineHeight: 1.3,
  },
  sectionSubtitle: {
    mt: 0.25,
    fontSize: 13,
    fontFamily: designFontFamily,
    color: designColors.slate600,
  },
};

export const CountBadge = ({ selected, total, sx = {} }) => {
  const active = selected > 0;
  return (
    <Mui.Box
      sx={{
        minWidth: 52,
        height: 26,
        px: 1.25,
        borderRadius: "999px",
        backgroundColor: active ? "#DDF5EE" : "#F1F5F9",
        color: active ? teal : designColors.slate600,
        fontSize: 13,
        fontWeight: 700,
        fontFamily: designFontFamily,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {selected}/{total}
    </Mui.Box>
  );
};

export const tealCheckboxSx = {
  p: 0.5,
  color: "#A0AEC0",
  "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: tealCheck },
  "&.Mui-disabled": { color: "#CBD5E1" },
  "&.Mui-disabled.Mui-checked": { color: "#7FBFBA" },
};

// Read-only checkbox for the View Role screens: same teal "checked" look as
// the editable one, but not interactive and not greyed out.
export const ReadOnlyCheck = ({ checked }) => (
  <Mui.Checkbox
    checked={Boolean(checked)}
    tabIndex={-1}
    disableRipple
    size="small"
    sx={{ ...tealCheckboxSx, pointerEvents: "none" }}
    inputProps={{ "aria-readonly": true }}
  />
);

// Pill list with a "+N" overflow chip whose tooltip lists the hidden items.
export const ChipList = ({ items = [], max = 3, emptyLabel = "-" }) => {
  if (!items.length) {
    return (
      <Mui.Typography sx={{ fontSize: 14, color: designColors.slate400 }}>
        {emptyLabel}
      </Mui.Typography>
    );
  }
  const shown = items.slice(0, max);
  const hidden = items.slice(max);
  const chipSx = {
    height: 30,
    borderRadius: "999px",
    backgroundColor: designColors.white,
    border: `1px solid ${designColors.slate200}`,
    color: designColors.slate700,
    fontSize: 13,
    fontWeight: 500,
    fontFamily: designFontFamily,
    maxWidth: 160,
  };
  return (
    <Mui.Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
      {shown.map((item) => (
        <Mui.Chip key={item} label={item} size="small" sx={chipSx} />
      ))}
      {hidden.length ? (
        <Mui.Tooltip arrow slotProps={tooltipStyleProps} title={hidden.join(", ")}>
          <Mui.Chip
            label={`+${hidden.length}`}
            size="small"
            sx={{ ...chipSx, border: "none", backgroundColor: "transparent", minWidth: 0 }}
          />
        </Mui.Tooltip>
      ) : null}
    </Mui.Stack>
  );
};

// ---------------------------------------------------------------------------
// Left "Modules" list shared by the Edit/Create form and the full-page View.
// ---------------------------------------------------------------------------

export const ModuleList = ({
  products,
  activeProduct,
  onSelect,
  // Omit to render the read-only variant (no checkboxes).
  onToggle,
  footer,
}) => (
  <Mui.Stack spacing={0.75}>
    {products.map((product) => {
      const name = product?.product_name;
      const selected = getProductSelectedCount(product);
      const total = getProductTotalCount(product);
      const active = activeProduct === name;
      return (
        <Mui.Box
          key={name}
          onClick={() => onSelect(name)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSelect(name);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.25,
            py: 0.875,
            borderRadius: "10px",
            cursor: "pointer",
            border: `1px solid ${active ? "#BFE8DE" : "transparent"}`,
            backgroundColor: active ? "#ECFAF6" : "transparent",
            outline: "none",
            "&:hover": { backgroundColor: active ? "#ECFAF6" : designColors.slateBg },
            "&:focus-visible": { borderColor: teal },
          }}
        >
          {onToggle ? (
            <Mui.Checkbox
              checked={total > 0 && selected === total}
              indeterminate={selected > 0 && selected < total}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => onToggle(name, event.target.checked)}
              disabled={total === 0}
              sx={tealCheckboxSx}
              inputProps={{ "aria-label": `Select all of ${name}` }}
            />
          ) : null}
          <ProductIcon name={name} size={34} />
          <Mui.Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              fontWeight: 700,
              fontFamily: designFontFamily,
              color: designColors.slate800,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Mui.Typography>
          <CountBadge selected={selected} total={total} />
          <KeyboardArrowRight sx={{ color: designColors.slate500, fontSize: 22 }} />
        </Mui.Box>
      );
    })}
    {footer}
  </Mui.Stack>
);

// ---------------------------------------------------------------------------
// Permission matrix (right panel). Editable when the on* handlers are given.
// ---------------------------------------------------------------------------

const headCellSx = {
  fontSize: 13,
  fontWeight: 700,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  backgroundColor: designColors.slateBg,
  borderBottom: `1px solid ${designColors.slate200}`,
  py: 1.1,
  px: 1.5,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  fontSize: 13.5,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  borderBottom: `1px solid ${designColors.slate100}`,
  py: 0.5,
  px: 1.5,
};

const groupCellSx = {
  fontSize: 12.5,
  fontWeight: 700,
  fontFamily: designFontFamily,
  color: teal,
  backgroundColor: "#F2FBFA",
  borderBottom: "1px solid #D7EFEB",
  py: 0.75,
  px: 1.5,
};

export const PermissionMatrix = ({
  product,
  search = "",
  readOnly = false,
  onToggleSubmodule,
  onToggleAll,
  onPermissionChange,
  // (field, checked) -- the Edit / Approve header checkboxes grant or revoke
  // that permission on every sub-module of the module where it applies.
  onColumnChange,
  // Full-page views have room for wider View/Edit/Approve columns so the
  // checkboxes don't look cramped; they stay grouped on the right.
  wide = false,
}) => {
  const groups = groupProductRows(product, search);
  const allSubmodules = (product?.module_details ?? []).flatMap(
    (module) => module?.submodules ?? [],
  );
  const hasDescriptions = allSubmodules.some((submodule) =>
    getSubmoduleDescription(submodule),
  );
  const selectedCount = allSubmodules.filter(isSubmoduleSelected).length;
  const allSelected = allSubmodules.length > 0 && selectedCount === allSubmodules.length;
  const columnCount = (readOnly ? 0 : 1) + 1 + (hasDescriptions ? 1 : 0) + 3;

  // Rows a header checkbox can act on: Edit where edit applies; Approve on
  // every row, since Approve is offered on every sub-module (2026-10-01) --
  // the same rule the form's Select All uses.
  const columnTargets = {
    editor: allSubmodules.filter((submodule) => submodule?.edit_applicable !== false),
    first_level_approver: allSubmodules,
  };
  // An "All sub-modules" row at the top of the list holds these checkboxes.
  const showColumnRow = !readOnly && Boolean(onColumnChange);
  const renderColumnCheckbox = (label, field) => {
    const targets = columnTargets[field];
    const grantedCount = targets.filter((submodule) => submodule?.permissions?.[field]).length;
    const all = targets.length > 0 && grantedCount === targets.length;
    return (
      <Mui.Checkbox
        checked={all}
        indeterminate={grantedCount > 0 && !all}
        disabled={targets.length === 0}
        onChange={(event) => onColumnChange(field, event.target.checked)}
        sx={tealCheckboxSx}
        inputProps={{ "aria-label": `${all ? "Remove" : "Grant"} ${label} for all sub-modules` }}
      />
    );
  };
  // My Workspace is the employee's own side: its approver flag grants
  // applying for requests, so the column reads "Apply" there. Same
  // first_level_approver field either way; only the label changes.
  const approveLabel = product?.product_name === "My Workspace" ? "Apply" : "Approve";
  const permissionColumns = [
    ["View", null],
    ["Edit", "editor"],
    [approveLabel, "first_level_approver"],
  ];

  // applicableField null = always applicable (Approve is shown on every row).
  const renderAction = (submodule, moduleName, field, applicableField) => {
    const applicable = !applicableField || submodule?.[applicableField] !== false;
    const granted = Boolean(submodule?.permissions?.[field]);
    if (!applicable && !granted) {
      return (
        <Mui.Typography sx={{ fontSize: 13, color: designColors.slate300 }}>-</Mui.Typography>
      );
    }
    if (readOnly) return <ReadOnlyCheck checked={granted} />;
    const checkbox = (
      <Mui.Checkbox
        checked={granted}
        disabled={!applicable}
        onChange={(event) =>
          onPermissionChange(moduleName, submodule?.sub_module_name, field, event.target.checked)
        }
        sx={tealCheckboxSx}
        inputProps={{ "aria-label": `${field} ${submodule?.sub_module_name}` }}
      />
    );
    if (!applicable) {
      return (
        <Mui.Tooltip
          arrow
          slotProps={tooltipStyleProps}
          title="This action is no longer applicable to this sub-module, but remains granted by this role. Uncheck the sub-module to remove its permissions."
        >
          <span>{checkbox}</span>
        </Mui.Tooltip>
      );
    }
    return checkbox;
  };

  // View is never clicked directly (same as the screens before the
  // redesign): it mirrors the row's own selection checkbox, which grants
  // View when ticked and revokes the whole row when cleared.
  const renderView = (submodule) => {
    const granted = Boolean(submodule?.permissions?.viewer);
    if (readOnly) return <ReadOnlyCheck checked={granted} />;
    const applicable = submodule?.view_applicable !== false;
    if (!applicable && !granted) {
      return (
        <Mui.Typography sx={{ fontSize: 13, color: designColors.slate300 }}>-</Mui.Typography>
      );
    }
    const checkbox = (
      <Mui.Checkbox
        checked={granted && isSubmoduleSelected(submodule)}
        disabled
        // Not clickable, but drawn in the normal (enabled) colours -- the
        // muted disabled tint would read as "unavailable", which it isn't.
        sx={{
          ...tealCheckboxSx,
          "&.Mui-disabled": { color: "#A0AEC0" },
          "&.Mui-disabled.Mui-checked": { color: tealCheck },
        }}
        inputProps={{ "aria-label": `View ${submodule?.sub_module_name}` }}
      />
    );
    return checkbox;
  };

  return (
    <Mui.TableContainer
      sx={{
        border: `1px solid ${designColors.slate200}`,
        borderRadius: "10px",
        overflowX: "auto",
      }}
    >
      <Mui.Table size="small" sx={{ minWidth: hasDescriptions ? 640 : 460 }}>
        <Mui.TableHead>
          <Mui.TableRow>
            {readOnly ? null : (
              <Mui.TableCell sx={{ ...headCellSx, width: 52, px: 1 }}>
                <Mui.Checkbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  disabled={allSubmodules.length === 0}
                  onChange={(event) => onToggleAll(event.target.checked)}
                  sx={tealCheckboxSx}
                  inputProps={{ "aria-label": "Select all sub-modules" }}
                />
              </Mui.TableCell>
            )}
            <Mui.TableCell sx={{ ...headCellSx, width: hasDescriptions ? "24%" : "auto" }}>
              Sub-module
            </Mui.TableCell>
            {hasDescriptions ? <Mui.TableCell sx={headCellSx}>Description</Mui.TableCell> : null}
            {permissionColumns.map(([label]) => (
              <Mui.TableCell key={label} align="center" sx={{ ...headCellSx, width: wide ? 160 : 96 }}>
                {label}
              </Mui.TableCell>
            ))}
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {showColumnRow && groups.length > 0 ? (
            // First row: grant/revoke Edit or Approve on every sub-module.
            <Mui.TableRow>
              <Mui.TableCell sx={{ ...bodyCellSx, px: 1 }} />
              <Mui.TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: designColors.slate700 }}>
                All sub-modules
              </Mui.TableCell>
              {hasDescriptions ? <Mui.TableCell sx={bodyCellSx} /> : null}
              {permissionColumns.map(([label, field]) => (
                <Mui.TableCell key={label} align="center" sx={bodyCellSx}>
                  {field ? renderColumnCheckbox(label, field) : null}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          ) : null}
          {groups.length === 0 ? (
            <Mui.TableRow>
              <Mui.TableCell colSpan={columnCount} sx={{ ...bodyCellSx, py: 4, textAlign: "center", color: designColors.slate600 }}>
                {search.trim()
                  ? "No sub-modules match your search."
                  : "This module has no sub-modules configured."}
              </Mui.TableCell>
            </Mui.TableRow>
          ) : (
            groups.map(({ module, submodules, showHeader }) => (
              <React.Fragment key={module?.module_name}>
                {showHeader ? (
                  // The group title starts in the Sub-module column so it
                  // lines up with the sub-module names below it (the
                  // checkbox column stays an empty cell in the same tint).
                  <Mui.TableRow>
                    {readOnly ? null : <Mui.TableCell sx={{ ...groupCellSx, px: 1 }} />}
                    <Mui.TableCell colSpan={columnCount - (readOnly ? 0 : 1)} sx={groupCellSx}>
                      {module?.module_name}
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : null}
                {submodules.map((submodule) => {
                  const selected = isSubmoduleSelected(submodule);
                  return (
                    <Mui.TableRow
                      key={`${module?.module_name}-${submodule?.sub_module_name}`}
                      hover
                      sx={{ "&:last-child td": { borderBottom: "none" } }}
                    >
                      {readOnly ? null : (
                        <Mui.TableCell sx={{ ...bodyCellSx, px: 1 }}>
                          <Mui.Checkbox
                            checked={selected}
                            onChange={(event) =>
                              onToggleSubmodule(
                                module?.module_name,
                                submodule?.sub_module_name,
                                event.target.checked,
                              )
                            }
                            sx={tealCheckboxSx}
                            inputProps={{ "aria-label": `Select ${submodule?.sub_module_name}` }}
                          />
                        </Mui.TableCell>
                      )}
                      <Mui.TableCell sx={bodyCellSx}>
                        {submodule?.sub_module_name}
                      </Mui.TableCell>
                      {hasDescriptions ? (
                        <Mui.TableCell sx={{ ...bodyCellSx, color: designColors.slate600, fontSize: 13 }}>
                          {getSubmoduleDescription(submodule) || "-"}
                        </Mui.TableCell>
                      ) : null}
                      <Mui.TableCell align="center" sx={bodyCellSx}>
                        {renderView(submodule)}
                      </Mui.TableCell>
                      <Mui.TableCell align="center" sx={bodyCellSx}>
                        {renderAction(submodule, module?.module_name, "editor", "edit_applicable")}
                      </Mui.TableCell>
                      <Mui.TableCell align="center" sx={bodyCellSx}>
                        {renderAction(
                          submodule,
                          module?.module_name,
                          "first_level_approver",
                          null,
                        )}
                      </Mui.TableCell>
                    </Mui.TableRow>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </Mui.TableBody>
      </Mui.Table>
    </Mui.TableContainer>
  );
};

export const SearchField = ({ value, onChange, placeholder, sx = {} }) => (
  <Mui.TextField
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    size="small"
    sx={{
      minWidth: { xs: "100%", md: 300 },
      "& .MuiOutlinedInput-root": {
        minHeight: 42,
        borderRadius: "10px",
        backgroundColor: designColors.white,
        fontFamily: designFontFamily,
        fontSize: 14,
        "& fieldset": { borderColor: designColors.borderInput },
        "&:hover fieldset": { borderColor: designColors.borderInputHover },
        "&.Mui-focused fieldset": { borderColor: "#0D9488" },
      },
      "& .MuiInputBase-input::placeholder": { color: designColors.slate400, opacity: 1 },
      ...sx,
    }}
    InputProps={{
      startAdornment: (
        <Mui.InputAdornment position="start">
          <Search sx={{ color: designColors.slate600, fontSize: 20 }} />
        </Mui.InputAdornment>
      ),
    }}
  />
);


// ---------------------------------------------------------------------------
// Related items card ("Related User Groups" on View Role, "Related User
// Roles" on View User Group): a heading with a count chip, then one row per
// item. Rows open the item's own view panel when onOpen is given.
// items: [{ key, name, description, meta, data }] -- onOpen receives data.
// ---------------------------------------------------------------------------

const RELATED_ICON_COLORS = [
  { color: "#2563EB", bg: "#EFF6FF" },
  { color: "#16A34A", bg: "#F0FDF4" },
  { color: "#9333EA", bg: "#FAF5FF" },
  { color: "#EA580C", bg: "#FFF7ED" },
];

export const RelatedItemsCard = ({
  icon,
  iconColor,
  iconBg,
  title,
  subtitle,
  countLabel,
  emptyText,
  items = [],
  onOpen,
  wide = false,
  sx = {},
}) => {
  const clickable = Boolean(onOpen);
  return (
    <Mui.Box sx={sx}>
      <SectionHeading
        icon={icon}
        iconColor={iconColor}
        iconBg={iconBg}
        title={title}
        subtitle={subtitle}
        action={
          <Mui.Chip
            label={countLabel}
            size="small"
            sx={{
              alignSelf: { xs: "flex-start", md: "center" },
              fontWeight: 600,
              fontFamily: designFontFamily,
              backgroundColor: designColors.slateBg,
              border: `1px solid ${designColors.slate200}`,
            }}
          />
        }
      />
      <Mui.Stack spacing={1} sx={{ mt: 2 }}>
        {items.length === 0 ? (
          <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate600, py: 1 }}>
            {emptyText}
          </Mui.Typography>
        ) : (
          items.map((item, index) => {
            const palette = RELATED_ICON_COLORS[index % RELATED_ICON_COLORS.length];
            return (
              <Mui.Box
                key={item.key ?? item.name ?? index}
                onClick={clickable ? () => onOpen(item.data) : undefined}
                sx={{
                  display: "grid",
                  gridTemplateColumns: wide
                    ? { xs: "40px 1fr 24px", md: "40px minmax(0, 1.1fr) minmax(0, 2fr) auto 24px" }
                    : "40px 1fr auto 24px",
                  alignItems: "center",
                  columnGap: 1.75,
                  rowGap: 0.5,
                  px: 1.5,
                  py: 1.1,
                  borderRadius: "10px",
                  border: `1px solid ${designColors.slate200}`,
                  cursor: clickable ? "pointer" : "default",
                  "&:hover": clickable ? { backgroundColor: designColors.slateBg } : {},
                }}
              >
                <SectionIcon icon={icon} size={36} color={palette.color} bg={palette.bg} />
                <Mui.Box sx={{ minWidth: 0 }}>
                  <Mui.Typography sx={{ fontSize: 14, fontWeight: 700, color: designColors.slate800 }}>
                    {item.name}
                  </Mui.Typography>
                  {!wide && item.description ? (
                    <Mui.Typography noWrap sx={{ fontSize: 12.5, color: designColors.slate600 }}>
                      {item.description}
                    </Mui.Typography>
                  ) : null}
                </Mui.Box>
                {wide ? (
                  <Mui.Typography
                    noWrap
                    sx={{ display: { xs: "none", md: "block" }, fontSize: 13, color: designColors.slate600 }}
                  >
                    {item.description || "-"}
                  </Mui.Typography>
                ) : null}
                <Mui.Typography
                  sx={{
                    display: wide ? { xs: "none", md: "block" } : "block",
                    fontSize: 12.5,
                    color: designColors.slate600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.meta}
                </Mui.Typography>
                {clickable ? <KeyboardArrowRight sx={{ color: designColors.slate500 }} /> : <span />}
              </Mui.Box>
            );
          })
        )}
      </Mui.Stack>
    </Mui.Box>
  );
};

// "Used in N companies" -- shared by both related cards.
export const usedInCompaniesLabel = (count) =>
  `Used in ${count} compan${count === 1 ? "y" : "ies"}`;
